import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { Tooltip } from "react-tooltip";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import ApprovalDropVoteSummary from "./ApprovalDropVoteSummary";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
interface WaveDropRatingsProps {
  readonly drop: ApiDrop;
  readonly winningThreshold?: number | null | undefined;
}
const getFallbackAvatarLabel = (label: string) => {
  const withoutHexPrefix = label.replace(/^0x/i, "");
  const source = withoutHexPrefix || label;
  return source.slice(0, 2).toUpperCase();
};
const WaveDropRatings: React.FC<WaveDropRatingsProps> = t0 => {
  const $ = _c(45);
  const {
    drop,
    winningThreshold
  } = t0;
  const [activeRaterId, setActiveRaterId] = React.useState(null);
  const hasWinningThreshold = typeof winningThreshold === "number" && Number.isFinite(winningThreshold) && winningThreshold > 0;
  if (hasWinningThreshold) {
    let t1;
    if ($[0] !== drop || $[1] !== winningThreshold) {
      t1 = <ApprovalDropVoteSummary drop={drop} winningThreshold={winningThreshold} variant="final" />;
      $[0] = drop;
      $[1] = winningThreshold;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const hasUserVoted = userVote !== 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const t1 = `tw-font-medium ${totalVoteClass}`;
  let t2;
  if ($[3] !== totalVote) {
    t2 = formatNumberWithCommas(totalVote);
    $[3] = totalVote;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== drop.rating || $[9] !== drop.rating_prediction) {
    t4 = <DropVoteProgressing current={drop.rating} projected={drop.rating_prediction} compact={true} />;
    $[8] = drop.rating;
    $[9] = drop.rating_prediction;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== votingLabel) {
    t6 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</span>;
    $[14] = votingLabel;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="tw-flex tw-items-center tw-gap-x-2">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== activeRaterId || $[20] !== drop.id || $[21] !== drop.top_raters || $[22] !== votingLabel) {
    let t9;
    if ($[24] !== activeRaterId || $[25] !== drop.id || $[26] !== drop.top_raters.length || $[27] !== votingLabel) {
      t9 = (rater, index) => (() => {
        const raterLabel = rater.profile.handle ?? rater.profile.primary_address;
        const raterHref = `/${raterLabel}`;
        const tooltipId = `wave-drop-rater-${drop.id}-${rater.profile.id}`;
        const fallbackAvatarLabel = getFallbackAvatarLabel(raterLabel);
        const raterTooltipLabel = `${raterLabel} • ${formatNumberWithCommas(rater.rating)} ${votingLabel}`;
        const baseZIndex = drop.top_raters.length - index;
        const zIndex = activeRaterId === rater.profile.id ? drop.top_raters.length + 1 : baseZIndex;
        return <React.Fragment key={rater.profile.id}><Link href={raterHref} onClick={_temp} onMouseEnter={() => setActiveRaterId(rater.profile.id)} onMouseLeave={() => setActiveRaterId(null)} onFocus={() => setActiveRaterId(rater.profile.id)} onBlur={() => setActiveRaterId(null)} aria-label={raterTooltipLabel} title={raterTooltipLabel} data-tooltip-id={tooltipId} className="tw-relative tw-block tw-transition-transform hover:tw-scale-110 focus-visible:tw-rounded-md focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400/70" style={{
            zIndex
          }}>{rater.profile.pfp ? <Image src={getScaledImageUri(rater.profile.pfp, ImageScale.W_AUTO_H_50)} alt={`${raterLabel}'s avatar`} width={20} height={20} className="tw-h-5 tw-w-5 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-black" /> : <span aria-hidden="true" className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-md tw-bg-iron-800 tw-text-[9px] tw-font-semibold tw-uppercase tw-text-iron-300 tw-ring-1 tw-ring-black">{fallbackAvatarLabel}</span>}</Link><Tooltip id={tooltipId} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{raterLabel} • {formatNumberWithCommas(rater.rating)}{" "}{votingLabel}</Tooltip></React.Fragment>;
      })();
      $[24] = activeRaterId;
      $[25] = drop.id;
      $[26] = drop.top_raters.length;
      $[27] = votingLabel;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    t8 = drop.top_raters.map(t9);
    $[19] = activeRaterId;
    $[20] = drop.id;
    $[21] = drop.top_raters;
    $[22] = votingLabel;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <div className="tw-flex tw-items-center -tw-space-x-1.5">{t8}</div>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== drop) {
    t10 = <ParticipationDropVoteDetailsTrigger drop={drop} />;
    $[31] = drop;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t9) {
    t11 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap">{t9}{t10}</div>;
    $[33] = t10;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== hasUserVoted || $[37] !== userVote || $[38] !== userVoteClass || $[39] !== votingLabel) {
    t12 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{userVote < 0 && "-"}{formatNumberWithCommas(Math.abs(userVote))} {votingLabel}</span></span></div>;
    $[36] = hasUserVoted;
    $[37] = userVote;
    $[38] = userVoteClass;
    $[39] = votingLabel;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t7) {
    t13 = <div className="tw-mt-1 tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-text-sm tw-leading-5">{t7}{t11}{t12}</div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t7;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
};
export default WaveDropRatings;
function _temp(event) {
  return event.stopPropagation();
}